import React from 'react';
import {
   FormApi as FinalFormApi,
   FORM_ERROR as FinalFormFormError
} from 'final-form';

export const FORM_ERROR = FinalFormFormError;

export interface AnyObject {
   [key: string]: any;
}

export type ValidationErrors = AnyObject | undefined;
export type SubmissionErrors = AnyObject | undefined;

export interface FormState<FormValues, InitialFormValues = Partial<FormValues>> {
   // by default: all values are subscribed. if subscription is specified, some values may be undefined
   active: undefined | keyof FormValues;
   dirty: boolean;
   dirtyFields: { [key: string]: boolean };
   dirtyFieldsSinceLastSubmit: { [key: string]: boolean };
   dirtySinceLastSubmit: boolean;
   error: any;
   errors: ValidationErrors;
   hasSubmitErrors: boolean;
   hasValidationErrors: boolean;
   initialValues: InitialFormValues;
   invalid: boolean;
   modified?: { [key: string]: boolean };
   modifiedSinceLastSubmit: boolean;
   pristine: boolean;
   submitError: any;
   submitErrors: SubmissionErrors;
   submitFailed: boolean;
   submitSucceeded: boolean;
   submitting: boolean;
   touched?: { [key: string]: boolean };
   valid: boolean;
   validating: boolean;
   values: FormValues;
   visited?: { [key: string]: boolean };
}

export interface FieldState<FieldValue> {
   active?: boolean;
   blur: () => void;
   change: (value: FieldValue | undefined) => void;
   data?: AnyObject;
   dirty?: boolean;
   dirtySinceLastSubmit?: boolean;
   error?: any;
   focus: () => void;
   initial?: FieldValue;
   invalid?: boolean;
   length?: number;
   modified?: boolean;
   modifiedSinceLastSubmit?: boolean;
   name: string;
   pristine?: boolean;
   submitError?: any;
   submitFailed?: boolean;
   submitSucceeded?: boolean;
   submitting?: boolean;
   touched?: boolean;
   valid?: boolean;
   validating?: boolean;
   value?: FieldValue;
   visited?: boolean;
}

export type FieldValidator<FieldValue> = (
   value: FieldValue,
   allValues: any,
   meta?: FieldState<FieldValue>
) => any | Promise<any>;
export type GetFieldValidator<FieldValue> = () =>
   | FieldValidator<FieldValue>
   | undefined;
export type IsEqual = (a: any, b: any) => boolean;

export interface InternalFieldState<FieldValue> {
   active: boolean
   blur: () => void
   change: (value: any) => void
   data: AnyObject
   focus: () => void
   isEqual: IsEqual
   lastFieldState?: FieldState<FieldValue>
   length?: any
   modified: boolean
   modifiedSinceLastSubmit: boolean
   name: string
   touched: boolean
   validateFields?: string[]
   validators: {
      [index: number]: GetFieldValidator<FieldValue>
   }
   valid: boolean
   validating: boolean
   visited: boolean
}

export interface MutableState<FormValues, InitialFormValues = Partial<FormValues>> {
   fieldSubscribers: { [key: string]: Subscribers<FieldState<any>> }
   fields: {
      [key: string]: any
   }
   formState: any
   lastFormState?: FormState<FormValues, InitialFormValues>
}

export type Unsubscribe = () => void
export type Mutator<FormValues,InitialFormValues = Partial<FormValues>> = (args: any, state: MutableState<FormValues, InitialFormValues>) => any;

export interface FormApi<FormValues = Record<string, any>, InitialFormValues = Partial<FormValues>> {
   isAutoSaveForm: () => boolean;
   batch: (fn: () => void) => void;
   blur: (name: keyof FormValues) => void;
   change: <F extends keyof FormValues>(name: F, value?: FormValues[F]) => void;
   batchChanges(formValues: Partial<FormValues>): void;
   destroyOnUnregister: boolean;
   focus: (name: keyof FormValues) => void;
   initialize: (data: InitialFormValues | ((values: FormValues) => InitialFormValues)) => void;
   isValidationPaused: () => boolean;
   getFieldState: <F extends keyof FormValues>(
      field: F
   ) => FieldState<FormValues[F]> | undefined;
   getRegisteredFields: () => string[];
   getState: () => FormState<FormValues, InitialFormValues>;
   mutators: Record<string, (...args: any[]) => any>;
   pauseValidation: () => void;
   reset: (initialValues?: InitialFormValues) => void;
   restart: (initialValues?: InitialFormValues) => void;
   resetFieldStateAllFields: () => void;
   resetFieldState: (name: keyof FormValues) => void;
   resumeValidation: () => void;
   submit: () => Promise<FormValues | undefined> | undefined;
   subscribe: (
      subscriber: FormSubscriber<FormValues>,
      subscription: FormSubscription
   ) => Unsubscribe
}

export type SupportedInputs = 'input' | 'select' | 'textarea';

export type FieldMetaState<FieldValue> = Pick<FieldState<FieldValue>,
   Exclude<keyof FieldState<FieldValue>,
      'blur' | 'change' | 'focus' | 'name' | 'value'>>;

export interface FieldInputProps<FieldValue, T extends HTMLElement = HTMLElement>
   extends AnyObject {
   name: string;
   onBlur: (event?: React.FocusEvent<T>) => void;
   onChange: (event: React.ChangeEvent<T> | any) => void;
   onFocus: (event?: React.FocusEvent<T>) => void;
   type?: string;
   value: FieldValue;
   checked?: boolean;
   multiple?: boolean;
}

export interface FieldRenderProps<FieldValue,
   T extends HTMLElement = HTMLElement, InputValue = FieldValue> {
   input: FieldInputProps<InputValue, T>;
   meta: FieldMetaState<FieldValue>;

   [otherProp: string]: any;
}

export interface RenderableProps<T> {
   children?: ((props: T) => React.ReactNode) | React.ReactNode;
   component?: React.ComponentType<T> | SupportedInputs;
   render?: (props: T) => React.ReactNode;
}

export type FormSubscriber<FormValues, InitialFormValues = Partial<FormValues>> = Subscriber<FormState<FormValues, InitialFormValues>>

export type Subscription = { [key: string]: boolean }
export type Subscriber<V> = (value: V) => void

export type Subscribers<T> = {
   index: number
   entries: {
      [key: number]: {
         subscriber: Subscriber<T>
         subscription: Subscription
         notified: boolean
      }
   }
}

export interface FormSubscription {
   active?: boolean;
   dirty?: boolean;
   dirtyFields?: boolean;
   dirtyFieldsSinceLastSubmit?: boolean;
   dirtySinceLastSubmit?: boolean;
   modifiedSinceLastSubmit?: boolean;
   error?: boolean;
   errors?: boolean;
   hasSubmitErrors?: boolean;
   hasValidationErrors?: boolean;
   initialValues?: boolean;
   invalid?: boolean;
   modified?: boolean;
   pristine?: boolean;
   submitError?: boolean;
   submitErrors?: boolean;
   submitFailed?: boolean;
   submitting?: boolean;
   submitSucceeded?: boolean;
   touched?: boolean;
   valid?: boolean;
   validating?: boolean;
   values?: boolean;
   visited?: boolean;
}

export class FormApiWrapper<FormValues = Record<string, any>, InitialFormValues = Partial<FormValues>> implements FormApi<FormValues, InitialFormValues> {

   private formApi: FinalFormApi<FormValues, InitialFormValues>;

   constructor(formApi: FinalFormApi<FormValues, InitialFormValues>) {
      this.formApi = formApi;
      this.submit.bind(this);
   }

   isAutoSaveForm(): boolean {
      return this.formApi.mutators['setFormSubmitting'] !== undefined;
   }

   batch(fn: () => void): void {
      this.formApi.batch(fn);
   }

   blur(name: keyof FormValues): void {
      this.formApi.blur(name);
   }

   change<F extends keyof FormValues>(name: F, value: FormValues[F] | undefined): void {
      this.formApi.change(name, value);
   }

   batchChanges(formValues: Partial<FormValues>): void {
      this.batch(()=>{
         for (const formValuesKey in (formValues as any)) {
            if (Object.prototype.hasOwnProperty.call(formValues, formValuesKey)) {
               const fieldName = formValuesKey as keyof FormValues;
               this.change(fieldName, formValues[fieldName]);
            }
         }
      });
   }

   get destroyOnUnregister() {
      return this.formApi.destroyOnUnregister;
   }

   set destroyOnUnregister(value: boolean) {
      this.formApi.destroyOnUnregister = value;
   }

   focus(name: keyof FormValues): void {
      this.formApi.focus(name);
   }

   getFieldState<F extends keyof FormValues>(field: F): FieldState<FormValues[F]> | undefined {
      return this.formApi.getFieldState(field);
   }

   getRegisteredFields(): string[] {
      return this.formApi.getRegisteredFields();
   }

   getState(): FormState<FormValues, InitialFormValues> {
      return this.formApi.getState();
   }

   initialize(data: ((values: FormValues) => InitialFormValues) | InitialFormValues): void {
      this.formApi.initialize(data);
   }

   isValidationPaused(): boolean {
      return this.formApi.isValidationPaused();
   }

   get mutators() {
      return this.formApi.mutators;
   }

   pauseValidation(): void {
      this.formApi.pauseValidation();
   }

   reset(initialValues: InitialFormValues | undefined): void {
      this.formApi.reset(initialValues);
   }

   restart(initialValues: InitialFormValues | undefined): void {
      this.formApi.restart(initialValues);
   }

   resetFieldStateAllFields(): void {
      this.formApi.getRegisteredFields().forEach((fieldName) => {
         this.formApi.resetFieldState(fieldName as any);
      });
   }

   resetFieldState(name: keyof FormValues): void {
      this.formApi.resetFieldState(name);
   }

   resumeValidation(): void {
      this.formApi.resumeValidation();
   }

   submit(): Promise<FormValues | undefined> | undefined {
      return this.formApi.submit();
   }

   subscribe(
      subscriber: FormSubscriber<FormValues>,
      subscription: FormSubscription
   ): Unsubscribe {
      return this.formApi.subscribe(subscriber, subscription);
   }
}
